import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "flex align-items-center justify-content-between mx-0 mb-2" }
const _hoisted_3 = {
  key: 1,
  class: "flex col-12 align-items-center justify-content-between px-0"
}
const _hoisted_4 = { class: "group-events-hourly-container col-12 p-3 bg-white border-round-lg" }
const _hoisted_5 = { class: "form-container w-full relative border-round-lg bg-gray-100 px-1 pt-4 mb-5" }
const _hoisted_6 = { class: "p-error" }
const _hoisted_7 = {
  key: 1,
  class: "mt-3"
}

import { toRefs, ref, inject, Ref, computed, watch } from 'vue';
import SelectButton from 'primevue/selectbutton';
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';

import { useOrganizationContractsStore } from '@/store';
import { GroupEventInterface } from '@/types';
import { CONTRACT_ENUMS } from '@/enum';

interface Props {
  loading: boolean;
  groups: GroupEventInterface[];
  scope: string;
}
enum GROUP_EVENT_FORM_TYPE {
  PERCENTAGE = 0,
  FLAT_FEE = 1,
}

export default /*@__PURE__*/_defineComponent({
  __name: 'OrganizationContractGroupEvents',
  props: {
    loading: { type: Boolean },
    groups: {},
    scope: {}
  },
  setup(__props: any) {

const menu = [
  { id: GROUP_EVENT_FORM_TYPE.PERCENTAGE, label: 'Percentage' },
  { id: GROUP_EVENT_FORM_TYPE.FLAT_FEE, label: 'Flat Fee' },
];

const contractStore = useOrganizationContractsStore();
const props = __props;
const { loading, groups, scope } = toRefs(props);
const selectedMainForm = inject<Ref<CONTRACT_ENUMS.FORMS>>('selectedForm', ref(CONTRACT_ENUMS.FORMS.FLAT_FEE));
const canEdit = contractStore.canEditOrCreateContracts;
const formDisplayText = computed<string>(() => {
  if (selectedMainForm.value == CONTRACT_ENUMS.FORMS.FLAT_FEE) {
    return 'Flat Fee';
  }
  return 'Per Student';
});
const selectedForm = computed({
  get() {
    const percentage = groups.value.filter((group) => group.active).every((group) => !group.attendees_rate_is_fixed);

    return percentage ? GROUP_EVENT_FORM_TYPE.PERCENTAGE : GROUP_EVENT_FORM_TYPE.FLAT_FEE;
  },
  set(value) {
    groups.value.forEach((group) => {
      group.attendees_rate_is_fixed = value === GROUP_EVENT_FORM_TYPE.PERCENTAGE ? 0 : 1;
    });
  },
});

const errorData = computed(() => {
  const errors = v$.value.$each.$response.$errors;
  return errors?.length
    ? errors
        .map((e) => {
          return Object.values(e).flat();
        })
        .flat()
    : null;
});

const attendeeRateCheck: (value: number, data: GroupEventInterface) => boolean = (value, data) => {
  if (!data.active) return true;
  if (!data.attendees_rate) return false;

  return true;
};

const noShowRateCheck: (value: number, data: GroupEventInterface) => boolean = (value, data) => {
  if (!data.active) return true;
  if (!data.noshow_rate) return false;

  return true;
};

const visibleGroups = computed(() => {
  return groups.value.filter((group) => !group.hidden);
});

const rules = computed(() => {
  return {
    $each: helpers.forEach({
      attendees_rate: {
        greaterThanZero: helpers.withMessage('Rate must be greater than 0', attendeeRateCheck),
      },
      noshow_rate: {
        greaterThanZero: helpers.withMessage('Rate must be greater than 0', noShowRateCheck),
      },
    }),
  };
});

const v$ = useVuelidate(rules, visibleGroups, { $scope: scope.value, $autoDirty: true });

const unlimitedGroupEvents = computed({
  get() {
    const defaultEnabledContractTypes = [CONTRACT_ENUMS.FORMS.FLAT_FEE, CONTRACT_ENUMS.FORMS.PER_STUDENT];
    if (defaultEnabledContractTypes.includes(selectedMainForm.value)) {
      return true;
    }
    return groups.value[0].active;
  },
  set(value) {
    groups.value[0].active = value;

    // sets the 1, 2, and 3 group objects to inactive
    if (groups.value[0].active) {
      groups.value.forEach((group) => {
        if (group.number_of_attendees) {
          group.active = false;
        }
      });
    } else {
      groups.value[1].active = true;
    }
  },
});

// Watch function that resets the rate values for inactive, visible inputs
watch([selectedForm, unlimitedGroupEvents], () => {
  groups.value.forEach((group) => {
    if (!group.hidden) {
      group.attendees_rate = 0;
      group.noshow_rate = 0;
    }
  });
});
// Watch function to automatically set the 2 attendee toggle active if 3 is active.
// Also sets 1 attendee and unlimited active to false if either 2 or 3 attendee is chosen
// If unlimited, 2, or 3 is not active, the 1 attendee object becomes active
watch(
  [visibleGroups],
  () => {
    const twoOrThreeAttendeesSelected = visibleGroups.value.some((group) => group.active);
    const threeAttendeesSelected = visibleGroups.value.some((group) => group.active && group.number_of_attendees === 3);
    if (!twoOrThreeAttendeesSelected) {
      if (!unlimitedGroupEvents.value) groups.value[1].active = true;
      else groups.value[1].active = false;
    } else {
      if (threeAttendeesSelected) {
        groups.value[2].active = true;
      }
      groups.value[0].active = false;
      groups.value[1].active = false;
    }
  },
  { deep: true },
);

return (_ctx: any,_cache: any) => {
  const _component_Skeleton = _resolveComponent("Skeleton")!
  const _component_InputSwitch = _resolveComponent("InputSwitch")!
  const _component_Column = _resolveComponent("Column")!
  const _component_InputNumber = _resolveComponent("InputNumber")!
  const _component_DataTable = _resolveComponent("DataTable")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["group-events-container p-3 pb-0 w-full border-round-md bg-gray-50", { flat: _unref(selectedMainForm) !== _unref(CONTRACT_ENUMS).FORMS.HOURLY }])
  }, [
    _cache[9] || (_cache[9] = _createElementVNode("p", null, [
      _createElementVNode("b", null, "Group Events")
    ], -1)),
    (_unref(selectedMainForm) === _unref(CONTRACT_ENUMS).FORMS.HOURLY)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            (_unref(loading))
              ? (_openBlock(), _createBlock(_component_Skeleton, {
                  key: 0,
                  height: "24px",
                  width: "47px"
                }))
              : (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _cache[6] || (_cache[6] = _createElementVNode("label", { for: "is_unlimited" }, "Unlimited Group Events", -1)),
                  _createVNode(_component_InputSwitch, {
                    id: "is_unlimited",
                    modelValue: unlimitedGroupEvents.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((unlimitedGroupEvents).value = $event)),
                    class: "switch",
                    disabled: !_unref(canEdit)
                  }, null, 8, ["modelValue", "disabled"])
                ]))
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(_component_DataTable, {
                value: visibleGroups.value,
                "show-gridlines": false,
                "row-class": () => 'border-none',
                "data-key": "id"
              }, {
                empty: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                  _createTextVNode("No group event data", -1)
                ]))]),
                default: _withCtx(() => [
                  _createVNode(_component_Column, {
                    header: "# of Attendees",
                    field: "active",
                    "body-class": "flex w-full align-items-center border-none",
                    class: "bg-gray-100 col-6"
                  }, {
                    body: _withCtx(({ data }) => [
                      _createVNode(_component_InputSwitch, {
                        modelValue: data.active,
                        "onUpdate:modelValue": ($event: any) => ((data.active) = $event),
                        disabled: unlimitedGroupEvents.value || !_unref(canEdit),
                        class: "switch mr-2"
                      }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"]),
                      _createTextVNode(" " + _toDisplayString(data.number_of_attendees) + " Attendees ", 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_Column, {
                    header: "Billing",
                    field: "billing_percentage",
                    class: "bg-gray-100 border-none"
                  }, {
                    body: _withCtx(({ data, index }) => [
                      (selectedForm.value === GROUP_EVENT_FORM_TYPE.PERCENTAGE)
                        ? (_openBlock(), _createBlock(_component_InputNumber, {
                            key: 0,
                            modelValue: data.attendees_rate,
                            "onUpdate:modelValue": ($event: any) => ((data.attendees_rate) = $event),
                            suffix: "%",
                            class: _normalizeClass(["group-events-input border-round-sm", { 'p-invalid': _unref(v$).$each?.$response?.$errors[index]?.attendees_rate?.length }]),
                            min: 0,
                            max: 100,
                            disabled: !data.active || unlimitedGroupEvents.value || !_unref(canEdit),
                            onBlur: _unref(v$).$touch,
                            onFocus: _cache[1] || (_cache[1] = (e) => e?.target?.select())
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "class", "disabled", "onBlur"]))
                        : (_openBlock(), _createBlock(_component_InputNumber, {
                            key: 1,
                            modelValue: data.attendees_rate,
                            "onUpdate:modelValue": ($event: any) => ((data.attendees_rate) = $event),
                            class: _normalizeClass(["group-events-input border-round-sm", { 'p-invalid': _unref(v$).$each.$response.$errors[index]?.attendees_rate?.length }]),
                            mode: "currency",
                            locale: "en-US",
                            "min-fraction-digits": 2,
                            currency: "USD",
                            min: 0,
                            disabled: !data.active || unlimitedGroupEvents.value || !_unref(canEdit),
                            onBlur: _unref(v$).$touch,
                            onFocus: _cache[2] || (_cache[2] = (e) => e?.target?.select())
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "class", "disabled", "onBlur"]))
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_Column, {
                    header: "No Show / Late Cancel",
                    field: "no_show_percentage",
                    class: "bg-gray-100 col-3 border-none"
                  }, {
                    body: _withCtx(({ data, index }) => [
                      (selectedForm.value === GROUP_EVENT_FORM_TYPE.PERCENTAGE)
                        ? (_openBlock(), _createBlock(_component_InputNumber, {
                            key: 0,
                            modelValue: data.noshow_rate,
                            "onUpdate:modelValue": ($event: any) => ((data.noshow_rate) = $event),
                            suffix: "%",
                            class: _normalizeClass(["group-events-input border-round-sm", { 'p-invalid': _unref(v$).$each.$response.$errors[index]?.noshow_rate?.length }]),
                            min: 0,
                            max: 100,
                            disabled: !data.active || unlimitedGroupEvents.value || !_unref(canEdit),
                            onBlur: _unref(v$).$touch,
                            onFocus: _cache[3] || (_cache[3] = (e) => e?.target?.select())
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "class", "disabled", "onBlur"]))
                        : (_openBlock(), _createBlock(_component_InputNumber, {
                            key: 1,
                            modelValue: data.noshow_rate,
                            "onUpdate:modelValue": ($event: any) => ((data.noshow_rate) = $event),
                            class: _normalizeClass(["group-events-input border-round-sm", { 'p-invalid': _unref(v$).$each.$response.$errors[index]?.noshow_rate?.length }]),
                            min: 0,
                            mode: "currency",
                            currency: "USD",
                            locale: "en-US",
                            "min-fraction-digits": 2,
                            disabled: !data.active || unlimitedGroupEvents.value || !_unref(canEdit),
                            onBlur: _unref(v$).$touch,
                            onFocus: _cache[4] || (_cache[4] = (e) => e?.target?.select())
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "class", "disabled", "onBlur"]))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["value"])
            ]),
            _createVNode(_unref(SelectButton), {
              modelValue: selectedForm.value,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((selectedForm).value = $event)),
              class: "attendee-select-button",
              options: menu,
              "option-label": "label",
              "option-value": "id",
              disabled: !_unref(canEdit)
            }, null, 8, ["modelValue", "disabled"])
          ]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errorData.value, (error) => {
            return (_openBlock(), _createElementBlock("div", {
              key: error.$uid,
              class: "input-errors mt-1"
            }, [
              _createElementVNode("div", _hoisted_6, _toDisplayString(error.$message), 1)
            ]))
          }), 128))
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _createElementVNode("p", null, [
            _createElementVNode("i", null, _toDisplayString(formDisplayText.value), 1),
            _cache[8] || (_cache[8] = _createTextVNode(" contracts allow unlimited group events ", -1))
          ])
        ]))
  ], 2))
}
}

})